import React, { useEffect, useRef } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { IAssignmentDetails } from '@/components/interfaces/Assignment';
import cn from '@/utils/cn';
import useFlags from '@/hooks/useFlags';
import BottomRowFilters from './BottomRowFilters';
import TopRowFilters from './TopRowFilters';
import TitleRow from './TitleRow';
import StatusBox from '../StatusBox';
import QuestionNavigator from './QuestionNavigator';
import GameStatusBox from '../StatusBox/GameStatusBox';
import GradeFasterMenu from './GradeFasterMenu';

const StatusContent = ({ isGame }: { isGame?: boolean }) => {
  const { isQuestionMode } = useGradingStore();

  if (isQuestionMode) return <QuestionNavigator />;
  if (isGame) return <GameStatusBox />;

  return <StatusBox />;
};

const HeaderWithFilters = () => {
  const { assignment, isTeacher } = useGradingStore();
  const { aiGradingActive } = useFlags();
  const headerRef = useRef<HTMLDivElement>(null);

  // AIDEV-NOTE: WCAG 2.4.11 (PLAT-16959) - the sticky toolbar covers the top of the
  // viewport, so keyboard focus on elements scrolled to the top (e.g. per-question score
  // inputs) lands hidden behind it. scroll-padding-top on the document keeps the browser's
  // focus-scroll clear of the toolbar. Driven by ResizeObserver since the toolbar's height
  // grows when a validation error appears.
  useResizeObserver({
    onResize: ({ height }) => {
      document.documentElement.style.scrollPaddingTop = height ? `${height}px` : '';
    },
    ref: headerRef,
  });

  useEffect(() => () => {
    document.documentElement.style.scrollPaddingTop = '';
  }, []);

  const {
    assignment_details: assignmentDetails = {} as IAssignmentDetails,
  } = assignment || {};

  if (!assignment) return null;

  const isGame = ['Challenge', 'Game'].includes(assignmentDetails.exercise_type);
  const greyBorder = 'tw-box-border tw-border tw-border-solid tw-border-med-grey tw-rounded-lg';

  const renderFiltersBox = () => (
    <div className={`tw-flex tw-flex-row tw-flex-none ${greyBorder} tw-py-2.5 tw-px-4`}>
      <div className="tw-flex-start tw-font-bold tw-text-base tw-pr-6">
        Filters:
      </div>
      <div>
        <div className="tw-flex tw-gap-3 tw-mb-2">
          <TopRowFilters classrooms={assignment.classrooms} />
        </div>
        <div className="tw-flex tw-gap-3">
          <BottomRowFilters />
        </div>
      </div>
    </div>
  );

  const showFilters = isTeacher && !isGame;

  return (
    <div ref={headerRef} className={`tw-sticky tw-top-0 tw-z-20 tw-bg-[#f9f9f9] ${greyBorder} tw-p-5`}>
      <TitleRow
        assignmentName={assignmentDetails.title}
        dueDatetime={assignment.due_datetime}
        lessonLocation={assignment.assignment_location}
      >
        {aiGradingActive && isTeacher && <GradeFasterMenu />}
      </TitleRow>

      <div className="tw-flex tw-gap-2.5 tw-pt-4">
        {showFilters && renderFiltersBox()}
        <div
          className={cn(
            greyBorder,
            'tw-py-2.5 tw-px-4',
            {
              'tw-basis-full': !showFilters,
              'tw-flex-1 tw-min-w-0': showFilters,
            },
          )}
        >
          <StatusContent isGame={isGame} />
        </div>
      </div>
    </div>
  );
};

export default HeaderWithFilters;
